import { useCallback, useEffect, useRef, useState } from "react";

import { cardStats, type BrowseRow, type CardStats } from "@/lib/cards";
import { dayMonthMaybeYear } from "@/lib/dates";
import {
  NoteFieldRow,
  noteDraft,
  noteFieldsFor,
  noteUpdateBody,
} from "@/components/NoteFieldRow";
import { TagChips } from "@/components/TagChips";
import { GroupChips } from "@/components/groups/GroupChips";
import type { UseGroups } from "@/components/groups/useGroups";
import { forgetSuggestedTags } from "@/lib/tags";
import { usePhone } from "@/lib/usePhone";
import { useLightbox } from "@/components/shell/Lightbox";
import {
  attachMedia,
  clearMedia,
  getNote,
  mediaUrl,
  updateNote,
  type NoteDetail,
} from "@/lib/note";
import { isTextEntry } from "@/lib/dom";
import { SLOT_DRAG_TYPE, type SlotKey } from "@/surfaces/read/ImageSlots";
import { applyNoteSlotMove, noteDragPayload } from "@/surfaces/read/noteSlotDrag";
import {
  fieldForSlot,
  firstImage,
  mediaFilename,
  slotForPaste,
  suffixForSlot,
} from "@/surfaces/read/mediaSlots";
import { useDialogs } from "@/components/shell/Dialogs";
import { useShiftHeld } from "@/surfaces/read/useShiftHeld";

import { STATE_LABEL, dueLabel } from "./query";

/**
 * The dock: the full note for the focused row.
 *
 * The field column scrolls; the headword line above and the action bar below stay pinned. That is
 * the whole point of the layout — you can be twelve fields deep and still see what you are editing
 * and still reach Save.
 */

export interface NoteDockProps {
  row: BrowseRow;
  onSaved: () => void;
  onDelete: () => void;
  onSetDueDate: () => void;
  onReposition: () => void;
  onReschedule: () => void;
  /** Confusion groups (日本-601). Held by `Cards`, which already reads membership for every row. */
  groups: UseGroups;
}

export function NoteDock({
  row,
  groups,
  onSaved,
  onDelete,
  onSetDueDate,
  onReposition,
  onReschedule,
}: NoteDockProps) {
  // **The media column stacks under the fields on a phone.** It is 214px fixed — Image, Extra
  // image, audio and the review-history strip — which on a 390px sheet leaves the fields 140px and
  // sets the headword one character per line. The frames put the media below, where it is the same
  // column at full width.
  const isPhone = usePhone();
  const [note, setNote] = useState<NoteDetail | null>(null);
  const [stats, setStats] = useState<CardStats | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * A media write that lands after you have moved on must be dropped.
   *
   * `note` and `draft` are set from different places — `draft` only by the row effect below, `note`
   * also by the media slots — so a response for the previous row would pair THAT note with THIS
   * row's draft, and the next Ctrl+Enter would write these fields onto that note. Uploads made the
   * window seconds long (base64 + upload + collection write + sync), and `J` is one keystroke.
   */
  const shownNoteId = useRef(row.noteId);
  shownNoteId.current = row.noteId;
  const onNoteChanged = useCallback((updated: NoteDetail) => {
    if (updated.noteId !== shownNoteId.current) return;
    setNote(updated);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setNote(null);
    setStats(null);
    setDirty(false);
    setError(null);
    void getNote(row.noteId)
      .then((loaded) => {
        if (cancelled) return;
        setNote(loaded);
        setDraft(noteDraft(loaded));
      })
      .catch(() => {
        // "Loading the note…" forever reads as a hang; naming the failure lets you retry.
        if (!cancelled) setError("ノートを読み込めませんでした。同期ハブは起動していますか？");
      });
    void cardStats(row.cardId)
      .then((s) => !cancelled && setStats(s))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [row.noteId, row.cardId]);

  const save = useCallback(async () => {
    // `!note.editable` guards the request, not just the button: Ctrl+Enter reaches here too, and
    // the six fields below belong to one notetype only.
    if (!note || !dirty || !note.editable) return;
    setSaving(true);
    setError(null);
    try {
      await updateNote(note.noteId, noteUpdateBody(draft, note));
      setDirty(false);
      onSaved();
    } catch (e) {
      // The draft stays dirty on screen; a save that silently un-busies reads as saved.
      setError(e instanceof Error ? e.message : "保存できませんでした。");
    } finally {
      setSaving(false);
    }
  }, [note, dirty, draft, onSaved]);

  /**
   * Tags are written on the spot, not folded into `save()`.
   *
   * Two reasons and the second decides it. `save()` returns early on `!dirty`, so a tag-only change
   * would need to fake a dirty draft to persist at all — and it is gated on `note.editable`, while
   * tags are collection-level rather than notetype-level. The dock already promises a foreign
   * note's "images and scheduling still work"; tags belong on that side of the line.
   *
   * `updateNote` with only `tags` sends no fields, so the server's unknown-field check has nothing
   * to reject and this works on any notetype. It answers with the note, so nothing is re-fetched.
   *
   * **No `notifyCollectionChanged()` here — 日本-283 made that the server's job.** `updateNote`
   * carries this tab's `X-Client-Id`, so the frame it provokes is ignored here and acted on
   * everywhere else, which is right: `onNoteChanged` and `onSaved` have already brought this
   * surface up to date. Announcing locally as well would force a redundant refetch on top of the
   * `reload` two lines down.
   */
  const writeTags = async (next: string[]) => {
    if (!note) return;
    setTagBusy(true);
    setError(null);
    try {
      onNoteChanged(await updateNote(note.noteId, { tags: next }));
      forgetSuggestedTags();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "タグを変更できませんでした。");
    } finally {
      setTagBusy(false);
    }
  };

  // Ctrl+Enter saves. Del is NOT bound here: the design puts it in the header hint, but a bare Del
  // that hard-deletes a note while the caret is in a text field is a trap, so deletion stays a
  // deliberate click through a confirmation.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // `defaultPrevented` so the tag input can own Ctrl+Enter to commit a tag without also saving
      // the note. Read's panel has always checked this; this one did not, and needed to once
      // anything below it wanted the same chord.
      if (event.defaultPrevented) return;
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 flex-wrap items-baseline gap-[10px] px-[22px] pb-[10px] pt-[13px]">
        <span className="text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">ノート</span>
        {/* The notetype comes from the note, not a literal. It was hardcoded, which was harmless
            while one notetype existed and became a lie the moment others could be browsed. */}
        <span className="font-mono text-meta text-ink-4">
          #{row.noteId}
          {note?.noteModel ? ` · ${note.noteModel}` : ""}
        </span>
        <span className="flex-1" />
        <span className="font-mono text-meta text-ink-4">Ctrl+Enterで保存 · J/Kで次へ</span>
      </div>

      <div
        className={`flex min-h-0 flex-1 gap-4 pb-[18px] ${
          isPhone ? "flex-col overflow-auto px-[16px]" : "px-[22px]"
        }`}
      >
        <div className="flex min-w-0 flex-1 flex-col rounded-[var(--r-3)] border border-line bg-panel">
          <div className="flex shrink-0 flex-wrap items-baseline gap-[10px] border-b border-line-soft px-4 py-3">
            <span className="font-jp text-[27px]/[1.1] font-semibold">{row.headword}</span>
            <span className="font-jp text-[15px]/[1] text-ink-3">{row.reading}</span>
            <span className="rounded-[var(--r-2)] border border-line bg-void px-[7px] py-[3px] text-[14px]/[1.3] font-semibold text-ink-2">
              {STATE_LABEL[row.state]}
            </span>
            {row.lapses > 0 && (
              <span className="rounded-[var(--r-2)] border border-accent-line bg-accent-tint px-[7px] py-[3px] text-[14px]/[1.3] font-semibold text-accent-hi">
                失敗{row.lapses}回
              </span>
            )}
          </div>

          {/* The scrolling part. */}
          <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
            {error && <p className="text-meta text-[color:var(--ink-3)]">{error}</p>}
            {note === null ? (
              !error && <p className="text-secondary text-ink-3">ノートを読み込み中…</p>
            ) : (
              <div className="flex flex-col gap-[10px]">
                <div className="flex items-baseline gap-[10px]">
                  <span className="w-[78px] shrink-0 text-field-label font-semibold uppercase tracking-[.06em] text-ink-4">
                    要復習
                  </span>
                  <span className="text-body text-ink">
                    {dueLabel(row)}
                    {row.intervalDays > 0 && (
                      <span className="text-ink-3"> · 間隔{row.intervalDays}日</span>
                    )}
                    {row.duePosition !== null && (
                      <span className="text-ink-3">
                        {" "}
                        · 位置{row.duePosition.toLocaleString()}
                      </span>
                    )}
                  </span>
                </div>

                {/* A note of another notetype has none of the six fields below, so the inputs
                    render blank over a note with real content and Save 400s on `Unknown field(s)`.
                    The server validates before writing, so nothing is lost — but offering the
                    editor at all is the trap `editable` exists to close, and browsing such a note
                    only became possible once other notetypes could count as carded. */}
                {!note.editable && (
                  <p className="text-meta text-ink-3">
                    {/* One expression rather than two wrapped text lines: JSX turns an interior
                        newline into a space, which would read as `もの なので`. */}
                    {note.noteModel}
                    {" のノートです。これらのフィールドは別のノートタイプのものなので、ここでは編集できません。画像とスケジュールはそのまま使えます。"}
                  </p>
                )}

                {note.editable &&
                  noteFieldsFor(note).map(({ key, label, multiline }) => (
                    <NoteFieldRow
                      key={String(key)}
                      label={label}
                      multiline={multiline}
                      value={draft[key as string] ?? ""}
                      onChange={(next) => {
                        setDraft((d) => ({ ...d, [key as string]: next }));
                        setDirty(true);
                      }}
                    />
                  ))}

                {/* Outside the `editable` guard: tags are collection-level, so they work on a
                    foreign notetype exactly as its images and scheduling do. */}
                <TagChips
                  tags={note.tags}
                  busy={tagBusy}
                  onChange={(next) => void writeTags(next)}
                />
                <GroupChips noteId={note.noteId} groups={groups} />
              </div>
            )}
          </div>

          {/* Pinned. */}
          <div className="flex shrink-0 flex-wrap items-center gap-[9px] border-t border-line-soft px-4 py-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !dirty || !note?.editable}
              className="rounded-[var(--r-2)] bg-ink px-[13px] py-2 text-[14px]/[1] font-semibold text-void disabled:opacity-40"
            >
              {saving ? "保存中…" : "保存"}
              <span className="ml-[7px] font-mono text-[14px]/[1] opacity-60">Ctrl+Enter</span>
            </button>
            <SecondaryButton onClick={onSetDueDate}>期日を設定</SecondaryButton>
            <SecondaryButton onClick={onReposition}>並べ替え</SecondaryButton>
            <SecondaryButton onClick={onReschedule}>再スケジュール</SecondaryButton>
            <span className="flex-1" />
            <button
              type="button"
              onClick={onDelete}
              className="rounded-[var(--r-2)] border border-accent-line bg-accent-tint px-[13px] py-2 text-[14px]/[1] font-semibold text-accent-hi"
            >
              ノートを削除…
            </button>
          </div>
        </div>

        <MediaColumn
          note={note}
          stats={stats}
          stacked={isPhone}
          onNoteChanged={onNoteChanged}
          onError={setError}
        />
      </div>
    </div>
  );
}

function SecondaryButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[var(--r-2)] border border-line-strong bg-void px-[11px] py-2 text-[14px]/[1] font-semibold text-ink-2"
    >
      {children}
    </button>
  );
}

/**
 * The two image slots, in the order the column stacks them. `Ctrl+Shift+V` aims at the second.
 *
 * **The `key` is a wire value and the `label` is copy.** `fieldForSlot` maps the key to the note
 * field, so only the second column translates — and `label.toLowerCase()` went with the English,
 * because Japanese has no case and the call was silently a no-op.
 *
 * Keyed by `SlotKey`, like Read's — the note-field name is derived with `fieldForSlot` at the point
 * of use. Keying by field instead made the paste handler convert forward and then search back to
 * recover the label, and `NoteDetail` names these properties `image` / `extraImage` anyway.
 */
const IMAGE_SLOTS = [
  { key: "image", label: "画像" },
  { key: "extraImage", label: "追加画像" },
] as const;

/** `214px` beside the fields, or full width beneath them on a phone: Image, Extra image, audio,
 *  and the review-history strip. */
function MediaColumn({
  note,
  stacked,
  stats,
  onNoteChanged,
  onError,
}: {
  note: NoteDetail | null;
  stats: CardStats | null;
  /** Full width beneath the fields rather than a fixed column beside them. */
  stacked?: boolean;
  onNoteChanged: (note: NoteDetail) => void;
  /** A failed clear reports through the dock's own error line — the column has no room for one. */
  onError: (message: string | null) => void;
}) {
  const shiftHeld = useShiftHeld();
  const dialogs = useDialogs();
  const noteId = note?.noteId ?? null;
  /**
   * Which slot is mid-write, held here rather than inside each slot.
   *
   * Lifting it is what the paste handler needs — it fires at the column, not at a slot, so it has
   * to be able to say "this one is busy" about a component it is not inside. It also puts this
   * column into the same shape as `AddedPanel`'s `busySlot`, so the app's two image surfaces
   * converge rather than drift.
   */
  const [busySlot, setBusySlot] = useState<SlotKey | null>(null);

  // An upload still in flight when you press `J` would otherwise leave `storing…` painted on the
  // NEXT note's slot — a claim about a slot you never touched.
  useEffect(() => setBusySlot(null), [noteId]);

  /** Drop the busy flag only if it is still ours; a late write must not un-busy a newer one. */
  const clearBusyIfCurrent = (slot: SlotKey) =>
    setBusySlot((current) => (current === slot ? null : current));

  const clear = async (slot: SlotKey, label: string) => {
    if (noteId === null) return;
    // Confirmed, unlike the reading log's ×. That one drops a line you can recapture with a
    // hotkey; this is an image on a card you made months ago, and there is no undo behind it.
    if (
      !(await dialogs.confirm({
        title: `${label}を削除？`,
        body: "取り消せません。",
        confirmLabel: "削除",
        tone: "danger",
      }))
    )
      return;
    setBusySlot(slot);
    onError(null);
    try {
      onNoteChanged(await clearMedia(noteId, fieldForSlot(slot)));
    } catch (e) {
      // The image is still there. A × that un-busies in silence reads as "removed", and the
      // next thing you would do is wonder why the thumbnail came back.
      onError(e instanceof Error ? e.message : `${label}を削除できませんでした。`);
    } finally {
      clearBusyIfCurrent(slot);
    }
  };

  /**
   * Put a file on the field. Never confirmed — adding is reversible by the × beside it, and a
   * replace leaves the old file in the media store either way.
   *
   * The empty slot was a dashed box that did nothing, so an old card with no picture could not get
   * one from anywhere in the app; the endpoint behind this has existed all along with no caller.
   */
  const attach = async (slot: SlotKey, file: File | null) => {
    if (noteId === null || !file) return;
    const label = IMAGE_SLOTS.find((s) => s.key === slot)!.label;
    setBusySlot(slot);
    onError(null);
    try {
      onNoteChanged(
        await attachMedia(
          noteId,
          fieldForSlot(slot),
          file,
          mediaFilename(file.type, Date.now(), suffixForSlot(slot)),
        ),
      );
    } catch (e) {
      onError(e instanceof Error ? e.message : `${label}を追加できませんでした。`);
    } finally {
      // Only if this write is still the one being waited on. The flag lives on the column now, so
      // an unconditional clear lets a slow upload from the note you have since left un-busy a slot
      // whose own upload is still running — it drops "storing…" and invites a second paste on top.
      clearBusyIfCurrent(slot);
    }
  };

  /**
   * Move an image between this card's two slots.
   *
   * **Not confirmed, unlike `clear` above**, and the contrast is the point: that one destroys an
   * image on a card you made months ago with no undo behind it, while every outcome here is
   * reversible by dragging back. A swap's two writes are one gesture, so `busySlot` covers the
   * whole move rather than each request.
   */
  const moveSlot = async (slot: SlotKey, payload: string) => {
    if (!note) return;
    setBusySlot(slot);
    onError(null);
    try {
      const updated = await applyNoteSlotMove(note, slot, payload);
      if (updated) onNoteChanged(updated);
    } catch (e) {
      onError(e instanceof Error ? e.message : "画像を移動できませんでした。");
    } finally {
      clearBusyIfCurrent(slot);
    }
  };

  /**
   * Ctrl+V lands on the focused card's Image, Ctrl+Shift+V on its Extra image.
   *
   * **No dependency array, deliberately.** `attach` closes over `noteId`, and a stale closure would
   * write the image onto the note you were looking at a moment ago. `AddedPanel` registers the same
   * way for the same reason.
   *
   * **This is not a third contender for the window paste.** Read owns two listeners that arbitrate
   * by hand — registration order makes `defaultPrevented` useless there, so `Read` yields to
   * `AddedPanel` by checking that nothing is expanded. Cards and Read never co-mount, so this one is
   * alone for as long as it exists; that is the invariant, stated here as they state theirs.
   *
   * **`note.editable` is deliberately not a gate.** The field column above already refuses to edit a
   * foreign notetype while promising its "images and scheduling still work", and this is the images.
   *
   * The stale-write guard is upstream and already correct: `onNoteChanged` drops a response whose
   * note is no longer the one on screen, so paste-then-`J` still writes to the right note and only
   * the repaint is dropped — which is the point.
   */
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (noteId === null) return;
      // A browser copy carries text/html AND an image — the very case `firstImage` exists for.
      // Without this, pasting a definition into the English field would swallow the text and hang
      // the page's illustration on the card instead.
      if (isTextEntry(event.target)) return;
      const file = firstImage(event.clipboardData?.files);
      if (!file) return;
      event.preventDefault();
      // The clipboard event carries no modifier state, so the last keydown decides the slot. Routed
      // through the same helper Read uses, so Ctrl+Shift+V cannot come to mean different things on
      // the two surfaces.
      void attach(slotForPaste(shiftHeld.current), file);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  return (
    <div
      className={`flex flex-col gap-3 ${
        stacked ? "w-full shrink-0" : "w-[214px] shrink-0 overflow-auto"
      }`}
    >
      {IMAGE_SLOTS.map(({ key, label }) => (
        <MediaSlot
          key={key}
          label={label}
          filename={note?.[key].filename ?? null}
          noteId={noteId}
          busy={busySlot === key}
          onAttach={(file) => void attach(key, file)}
          onClear={() => void clear(key, label)}
          dragPayload={() => noteDragPayload(note, key)}
          onSlotDrop={(payload) => void moveSlot(key, payload)}
        />
      ))}

      {note?.audio.filename && (
        <audio
          controls
          preload="none"
          src={mediaUrl(note.audio.filename)}
          className="h-8 w-full"
        />
      )}

      {stats && stats.history.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-field-label font-semibold uppercase tracking-[.06em] text-ink-4">
            復習履歴
          </span>
          {/* Full-height green for a pass, 46% red for a fail — the shape of a leech is visible
              at a glance without reading a single number. */}
          <div className="flex h-[34px] items-end gap-[2px]">
            {stats.history.map((entry, i) => (
              <span
                key={i}
                // Review history goes back years, so this is where the year earns its place most.
                title={`${dayMonthMaybeYear(new Date(entry.at * 1000))} · ${
                  entry.passed ? "合格" : "不可"
                }`}
                className="min-w-[2px] flex-1 rounded-[var(--r-2)]"
                style={{
                  height: entry.passed ? "100%" : "46%",
                  background: entry.passed
                    ? "var(--ink-3)"
                    : "var(--accent)",
                }}
              />
            ))}
          </div>
          <span className="font-mono text-[14px]/[1.4] text-ink-4">
            不可{stats.lapses} · 合格{stats.reviews - stats.lapses} · 間隔
            {stats.intervalDays}日
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * One slot. Presentational: `MediaColumn` owns the writes and the busy flag, because the paste
 * handler fires at the column and has to be able to drive a slot from outside it.
 */
function MediaSlot({
  label,
  filename,
  noteId,
  busy,
  onAttach,
  onClear,
  dragPayload,
  onSlotDrop,
}: {
  label: string;
  filename: string | null;
  noteId: number | null;
  busy: boolean;
  onAttach: (file: File | null) => void;
  onClear: () => void;
  /** This slot, serialized for a drag, or null when it is empty. */
  dragPayload: () => string | null;
  onSlotDrop: (payload: string) => void;
}) {
  const lightbox = useLightbox();
  const input = useRef<HTMLInputElement>(null);

  const dropProps = {
    onDragOver: (e: React.DragEvent) => {
      if (noteId === null) return;
      e.preventDefault();
    },
    onDrop: (e: React.DragEvent) => {
      if (noteId === null) return;
      e.preventDefault();
      // A slot payload wins over the file list, as in `ImageSlots` and for the same reason: a drag
      // that began on an <img> still offers a URL, so checking files first would turn a move
      // between this card's two slots into a re-download of a file it already has.
      const payload = e.dataTransfer.getData(SLOT_DRAG_TYPE);
      if (payload) {
        onSlotDrop(payload);
        return;
      }
      onAttach(firstImage(e.dataTransfer.files));
    },
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-field-label font-semibold uppercase tracking-[.06em] text-ink-4">
        {label}
      </span>
      {filename ? (
        <div className="relative" {...dropProps}>
          <button
            type="button"
            onClick={() => lightbox.open(filename)}
            aria-label={`${label}を拡大: ${filename}`}
            title={`${filename} — クリックで拡大。ファイルをドロップまたは貼り付けで差し替え。`}
            className="block w-full overflow-hidden rounded-[var(--r-2)] border border-line-strong"
            style={{ height: "var(--media-slot-h)", cursor: "zoom-in" }}
          >
            <img
              src={mediaUrl(filename)}
              alt=""
              className="h-full w-full object-cover"
              draggable
              onDragStart={(e) => {
                const payload = dragPayload();
                if (!payload) {
                  e.preventDefault();
                  return;
                }
                e.dataTransfer.setData(SLOT_DRAG_TYPE, payload);
                e.dataTransfer.effectAllowed = "move";
              }}
            />
          </button>
          {/* Its own target in the corner, so it cannot be hit while aiming at the image. */}
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            aria-label={`${label}を削除`}
            title="削除"
            className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-panel/90 text-[14px]/[1] font-semibold text-ink-3 hover:text-accent-text"
          >
            ×
          </button>
        </div>
      ) : (
        // Was an inert dashed box, so a card with no picture could not be given one anywhere in the
        // app. Same height as a filled slot: at half that it read as a label rather than as
        // somewhere to aim a file, which is why its own shorter token is gone.
        <button
          type="button"
          disabled={noteId === null || busy}
          onClick={() => input.current?.click()}
          {...dropProps}
          aria-label={`${label}を追加`}
          title="クリック・ドロップ・貼り付け（Ctrl+V — 追加スロットは Ctrl+Shift+V）"
          className="flex w-full items-center justify-center rounded-[var(--r-2)] border-[1.5px] border-dashed border-line-strong text-[14px] text-ink-4 disabled:cursor-default"
          style={{
            height: "var(--media-slot-h)",
            cursor: noteId === null ? "default" : "pointer",
          }}
        >
          {busy ? "保存中…" : noteId === null ? "なし" : "クリックまたはドロップ"}
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          onAttach(firstImage(e.currentTarget.files));
          // Reset, so choosing the same file twice still fires a change.
          e.currentTarget.value = "";
        }}
      />
    </div>
  );
}
